import { LuArrowLeftRight, LuRefreshCcw } from 'react-icons/lu';
import { MdCreditCard, MdPix } from 'react-icons/md';
import AmericanExpressLogo from '../../../assets/icons/logo_amex.svg?react';
import EloLogo from '../../../assets/icons/logo_elo.svg?react';
import MastercardLogo from '../../../assets/icons/logo_mastercard.svg?react';
import VisaLogo from '../../../assets/icons/logo_visa.svg?react';
import { BigButton } from '../../../components/BigButton';
import { Checkbox } from '../../../components/Checkbox';
import { Format } from '../../../helpers/formatters';
import { useAddressModalStore } from '../../../store/address/addressModalStore';
import {
    usePaymentModalStore,
    type PaymentMethod,
} from '../../../store/payment/paymentModalStore';
import { useShoppingCartStore } from '../../../store/shoppingCart/shoppingCartStore';
import { TermText } from '../styles';
import { PaymentButton } from './PaymentMethodBox/styles';
import {
    CardBox,
    InstallmentsBox,
    PaymentContainer,
    PaymentSection,
    PaymentTitle,
} from './styles';

interface Props {
    paymentMethod: PaymentMethod;
    regulation: {
        isVisibleModal: boolean;
        isReaded: boolean;
    };
    isPendingCheckout: boolean | undefined;
    handleChangePaymentMethod: (method: PaymentMethod) => void;
    handleSubmitCheckout: () => void;
    canFinishCheckout: boolean;
    setRegulation: (d: { isReaded: boolean; isVisibleModal: boolean }) => void;
    isCortesia: boolean;
}

export const getCardBrandIcon = (method: string) => {
    switch (method) {
        case 'MASTERCARD':
            return <MastercardLogo />;
        case 'VISA':
            return <VisaLogo />;
        case 'AMERICAN_EXPRESS':
            return <AmericanExpressLogo />;
        case 'ELO':
            return <EloLogo />;
    }
};

export default function PaymentMethodInputs({
    paymentMethod,
    handleChangePaymentMethod,
    regulation,
    isPendingCheckout,
    handleSubmitCheckout,
    setRegulation,
    isCortesia,
}: Props) {
    const [cardCredentials, cardInstallments] = usePaymentModalStore(state => [
        state.card,
        state.installments,
    ]);
    const paymentModal = usePaymentModalStore();
    const addressModal = useAddressModalStore();

    const shoppingCartTotalValue =
        useShoppingCartStore(state => state.cartValues)?.reduce(
            (acc, product) => acc + (product.value - (product.discountValue ?? 0)),
            0
        ) ?? 0;

    function handleChangeInstallments() {
        paymentModal.update({
            isVisible: true,
            isntallmentsVisibility: true,
        });
    }

    const CheckboxComponent = () => (
        <Checkbox
            statusCheck={regulation.isReaded}
            onClickCheck={() =>
                setRegulation({
                    isReaded: !regulation.isReaded,
                    isVisibleModal: false,
                })
            }
            variant={regulation.isReaded ? 'secondary' : 'primary'}
            descriptionCheck={
                <>
                    Li e aceito os{' '}
                    <TermText
                        onClick={() =>
                            window.open(
                                'https://cdn.surflandbrasil.com.br/internal/regulamento.pdf',
                                '_blank'
                            )
                        }
                    >
                        termos deste regulamento.
                    </TermText>
                </>
            }
        />
    );

    const checkCanFinishPurchase = () => {
        if (paymentModal.isPending) return false;
        if (!regulation.isReaded && !isCortesia) return false;
        if (paymentMethod === null && !isCortesia) return false;
        if (paymentMethod === 'card' && !cardCredentials) return false;
        if (!addressModal.isValid) return false;
        return true;
    };

    if (isCortesia) {
        return (
            <PaymentSection>
                <PaymentContainer>
                    <BigButton
                        bgColor={
                            checkCanFinishPurchase() ? 'primary' : 'secondary'
                        }
                        color="primary"
                        children={'Resgatar voucher'}
                        isLoading={isPendingCheckout || paymentModal.isPending}
                        onClick={handleSubmitCheckout}
                        cursorBlocked={!checkCanFinishPurchase()}
                    />
                </PaymentContainer>
            </PaymentSection>
        );
    }

    return (
        <PaymentSection>
            <PaymentTitle>
                <h3>Método de pagamento</h3>
                {paymentMethod ? (
                    <p onClick={() => handleChangePaymentMethod(null)}>
                        Trocar
                    </p>
                ) : (
                    <p />
                )}
            </PaymentTitle>
            <PaymentContainer>
                <div className="row">
                    {(paymentMethod === 'pix' ||
                        !paymentMethod ||
                        !cardCredentials) && (
                        <PaymentButton
                            allWidth={true}
                            bgColor={
                                paymentMethod === 'pix' ? 'pix' : 'disabled'
                            }
                            color={paymentMethod === 'pix' ? 'white' : 'black'}
                            onClick={() => {
                                handleChangePaymentMethod('pix');
                            }}
                            children={
                                <>
                                    <MdPix
                                        className="payment-icon"
                                        color={
                                            paymentMethod === 'pix'
                                                ? 'white'
                                                : 'black'
                                        }
                                    />
                                    Pix
                                </>
                            }
                        />
                    )}
                    {(paymentMethod === 'card' || !paymentMethod) &&
                        !cardCredentials && (
                            <PaymentButton
                                allWidth={true}
                                bgColor={'disabled'}
                                color={'black'}
                                onClick={() => {
                                    handleChangePaymentMethod('card');
                                }}
                                children={
                                    <>
                                        <MdCreditCard className="payment-icon" />
                                        Cartão
                                    </>
                                }
                            />
                        )}
                    {paymentMethod === 'card' && cardCredentials && (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexDirection: 'column',
                                gap: '.75rem',
                            }}
                        >
                            <CardBox>
                                <div className="card-brand">
                                    {getCardBrandIcon(cardCredentials.brand)}
                                </div>
                                <div className="sub-session">
                                    <div className="card-pile">
                                        {cardCredentials.subMethod ===
                                        'CREDIT' ? (
                                            <span>Cartão de crédito</span>
                                        ) : (
                                            <span>Cartão de débito</span>
                                        )}
                                        <p className="last-digits">
                                            ****{' '}
                                            {cardCredentials.number.slice(-4)}
                                        </p>
                                    </div>
                                    <LuRefreshCcw
                                        className="refresh-icon"
                                        onClick={() =>
                                            handleChangePaymentMethod('card')
                                        }
                                    />
                                </div>
                            </CardBox>
                            <InstallmentsBox>
                                {cardInstallments === 1 || !cardInstallments ? (
                                    <p>
                                        À vista - Total:{' '}
                                        {Format.currency(
                                            shoppingCartTotalValue
                                        )}
                                    </p>
                                ) : (
                                    <p>{cardInstallments}x sem juros</p>
                                )}
                                <LuArrowLeftRight
                                    className="installments-icon"
                                    onClick={handleChangeInstallments}
                                />
                            </InstallmentsBox>
                        </div>
                    )}
                </div>
                <CheckboxComponent />
                <BigButton
                    bgColor={checkCanFinishPurchase() ? 'primary' : 'secondary'}
                    color="primary"
                    children={'Finalizar compra'}
                    isLoading={isPendingCheckout || paymentModal.isPending}
                    onClick={handleSubmitCheckout}
                    cursorBlocked={!checkCanFinishPurchase()}
                />
            </PaymentContainer>
        </PaymentSection>
    );
}
